import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PHONE_DISPLAY, PHONE_TEL, SERVICE_OPTIONS } from "@/components/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", contact: "", service: SERVICE_OPTIONS[0], details: "" };

const inputClass =
  "w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white focus:border-[#d4af37] outline-none transition-colors";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function BusinessHours() {
  const [hours, setHours] = useState(null);
  useEffect(() => {
    axios.get(`${API}/settings/public`).then((r) => setHours(r.data.business_hours)).catch(() => null);
  }, []);
  if (!hours) return null;
  const open = DAYS.filter((d) => hours[d] && !hours[d].closed && hours[d].open && hours[d].close);
  if (open.length === 0) return null;
  return (
    <div className="mt-6 border-t border-[#d4af37]/15 pt-5" data-testid="business-hours">
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-3">Business Hours</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono">
        {open.map((d) => (
          <p key={d} className="text-zinc-400">
            <span className="text-zinc-200 capitalize">{d.slice(0, 3)}</span> · {hours[d].open}–{hours[d].close}
          </p>
        ))}
      </div>
    </div>
  );
}

export default function ContactSection() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      setForm(EMPTY);
      toast.success("Request received — DuroTech28 will reach out shortly.");
    } catch (err) {
      toast.error(`Could not submit right now. Please call ${PHONE_DISPLAY}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 max-w-4xl mx-auto" data-testid="contact-section">
      <div className="rounded-2xl p-8 sm:p-12 bg-gradient-to-b from-[#14141a] to-[#0d0d10] border border-[#d4af37]/30 shadow-2xl">
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="text-xs font-mono uppercase tracking-widest text-[#d4af37]">Fast Turnaround</span>
          <h2 className="font-display text-3xl sm:text-4xl uppercase text-white mt-1">Request Service or Quote</h2>
          <p className="text-xs text-zinc-400 mt-2 font-mono">
            Direct call:{" "}
            <a href={PHONE_TEL} data-testid="contact-call-link" className="text-[#d4af37] font-semibold hover:underline">
              {PHONE_DISPLAY}
            </a>{" "}
            • DFW Metroplex
          </p>
          <BusinessHours />
        </div>

        {submitted ? (
          <div className="text-center py-10" data-testid="quote-form-success">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full border border-[#d4af37]/50 flex items-center justify-center">
              <svg className="w-7 h-7 text-[#d4af37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="font-display text-2xl uppercase text-white mb-2">Request Received</h3>
            <p className="text-xs text-zinc-400 font-mono max-w-sm mx-auto leading-relaxed">
              Your service request is in. For immediate help, call{" "}
              <a href={PHONE_TEL} className="text-[#d4af37] hover:underline">{PHONE_DISPLAY}</a>.
            </p>
            <button
              type="button"
              data-testid="quote-form-another-btn"
              onClick={() => setSubmitted(false)}
              className="mt-6 px-6 py-2.5 rounded border border-[#d4af37]/40 text-xs font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors"
            >
              Submit Another Request
            </button>
          </div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono" onSubmit={handleSubmit} data-testid="quote-form">
            <div>
              <label htmlFor="quote-name" className="block text-zinc-400 mb-1">Your Name</label>
              <input
                id="quote-name"
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Alex Morgan"
                data-testid="quote-form-name-input"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="quote-contact" className="block text-zinc-400 mb-1">Phone or Email</label>
              <input
                id="quote-contact"
                type="text"
                required
                value={form.contact}
                onChange={update("contact")}
                placeholder="469-000-0000"
                data-testid="quote-form-contact-input"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="quote-service" className="block text-zinc-400 mb-1">Service Required</label>
              <select
                id="quote-service"
                value={form.service}
                onChange={update("service")}
                data-testid="quote-form-service-select"
                className={inputClass}
              >
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="quote-details" className="block text-zinc-400 mb-1">Project Details</label>
              <textarea
                id="quote-details"
                rows="3"
                value={form.details}
                onChange={update("details")}
                placeholder="Describe your residential or commercial project scope..."
                data-testid="quote-form-details-textarea"
                className={inputClass}
              ></textarea>
            </div>
            <div className="sm:col-span-2 mt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-form-submit-button"
                className="w-full py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25 disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Service Request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
